import { useState, useEffect } from "react";
import { IonModal, IonContent } from "@ionic/react";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { useAuth } from "../Context/AuthContext";
import { apiFetch } from "../utils/api";

export default function AliasModal() {
  // AuthContext only exposes { user, login, logout } — no token, no setAlias.
  // We read the token directly from storage (same pattern as RolePickerModal).
  const { user, login } = useAuth() || {};
  const [open,       setOpen]       = useState(false);
  const [aliasInput, setAliasInput] = useState("");
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");

  function getToken() {
    try {
      return (
        sessionStorage.getItem("gt_token") ||
        localStorage.getItem("gt_token") ||
        localStorage.getItem("token") ||
        null
      );
    } catch (e) { return null; }
  }

  // Open via global event (triggered from Header click)
  useEffect(function() {
    function handler() {
      if (!getToken()) return;
      setAliasInput((user && user.alias) || "");
      setError("");
      setOpen(true);
    }
    window.addEventListener("open-alias", handler);
    return function() { window.removeEventListener("open-alias", handler); };
  }, [user]);

  // ESC to close
  useEffect(function() {
    function handler(e) { if (e.key === "Escape") setOpen(false); }
    window.addEventListener("keydown", handler);
    return function() { window.removeEventListener("keydown", handler); };
  }, []);

  async function handleSave() {
    if (!aliasInput.trim()) { setError("Please enter a name."); return; }
    await Haptics.impact({ style: ImpactStyle.Medium }).catch(function() {});
    setLoading(true);
    setError("");
    try {
      var t = getToken();
      if (!t) { setError("Not logged in. Please log in again."); setLoading(false); return; }

      var res = await apiFetch("/api/set-alias", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + t },
        body: JSON.stringify({ alias: aliasInput.trim() }),
      });

      var data = {};
      try { data = await res.json(); } catch (e) { /* non-JSON response */ }

      if (!res.ok) {
        setError(data.error || "Failed to save. Try again.");
        return;
      }

      // Re-issue token with new alias baked in — login() updates AuthContext + storage
      if (data.token) {
        login(data.token);
      }
      await Haptics.notification({ type: NotificationType.Success }).catch(function() {});
      setOpen(false);
    } catch (e) {
      setError("Network error. Try again.");
    } finally {
      setLoading(false);
    }
  }

  var isOpen = open && !!getToken();

  return (
    <IonModal isOpen={isOpen} onDidDismiss={function() { setOpen(false); }}
      breakpoints={[0, 1]} initialBreakpoint={1}
      style={{"--background": "#09090b", "--border-radius": "16px 16px 0 0"}}>
    <IonContent style={{"--background": "#09090b"}}>
      <div className="w-full p-6 space-y-5">
        <div>
          <p className="text-lime-400 text-xs font-mono uppercase tracking-widest mb-1">
            Profile
          </p>
          <h2 className="text-2xl font-bold uppercase tracking-tight">
            Change your name
          </h2>
          <p className="text-zinc-500 text-sm mt-1">
            This is how others see you in games.
          </p>
        </div>

        <input
          placeholder="Your alias"
          value={aliasInput}
          onChange={function(e) { setAliasInput(e.target.value); }}
          onKeyDown={function(e) { if (e.key === "Enter") handleSave(); }}
          className="w-full bg-zinc-900 border border-zinc-800 focus:border-lime-400 text-white text-sm px-4 py-3 outline-none transition-colors duration-200 placeholder-zinc-600 block"
        />

        {error && (
          <p className="text-red-400 text-xs font-mono border border-red-900 bg-red-950/30 px-3 py-2">
            {error}
          </p>
        )}

        <button
          onClick={handleSave}
          disabled={loading || !aliasInput.trim()}
          className="w-full text-xs font-mono uppercase tracking-widest py-3 bg-lime-400 text-black font-bold hover:bg-lime-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
        >
          {loading ? "Saving..." : "Save"}
        </button>

        <button
          onClick={function() { setOpen(false); }}
          className="w-full text-xs font-mono uppercase tracking-widest py-3 border border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200 transition-all duration-200"
        >
          Cancel
        </button>
      </div>
    </IonContent>
    </IonModal>
  );
}
